import React from 'react';
import { useLocalization } from '../../../../../config/localization';
import Icon, { ICONS } from '../../../../ui/icons';
import WorkflowWidgetEditor from '../../../../ui/WorkflowWidgetEditor';
import type { WidgetsSectionProps } from './index';

export default function WidgetsSection({
  formData,
  onFormDataChange,
  workflowWidgets,
  widgetTypes,
  onWidgetToggle,
  onDisplayOrderChange,
  onNavigateToDynamicWidget,
  language
}: WidgetsSectionProps) {
  const t = useLocalization(language);

  // Convert widget toggle to string-based handler for WorkflowWidgetEditor
  // For new widgets that aren't in workflowWidgets yet, we use the widgetTypeId directly
  const handleWidgetToggle = (widgetTypeId: string) => {
    const numericId = Number(widgetTypeId);
    const existingWidget = workflowWidgets.find(w => w.widget_type_id === numericId);

    // Toggle: if exists and enabled -> disable, otherwise -> enable
    const newState = existingWidget ? !existingWidget.is_enabled : true;
    onWidgetToggle(numericId, newState);
  };

  // Convert display order change to string-based handler
  const handleDisplayOrderChange = (widgetTypeId: string, newOrder: number) => {
    const numericId = Number(widgetTypeId);
    onDisplayOrderChange(numericId, newOrder);
  };

  // Handle dynamic widget display order change
  const handleDynamicWidgetOrderChange = (newOrder: number) => {
    if (formData.dynamic_widget) {
      onFormDataChange('dynamic_widget', {
        ...formData.dynamic_widget,
        display_order: newOrder
      });
    }
  };

  const enabledCount = workflowWidgets.filter(w => w.is_enabled).length;
  const hasDynamicWidget = formData.dynamic_widget?.name && formData.dynamic_widget?.fields?.length > 0;

  return (
    <div className="space-y-4">
      {/* Section Header */}
      <section>
        <div className="flex items-center justify-between mb-2">
          <h3 className="text-lg font-semibold text-white flex items-center gap-2">
            <Icon name={ICONS.GRID} size={20} className="text-purple-400" />
            {t.admin?.widgetConfiguration || 'Widget Configuration'}
          </h3>
          <div className="flex items-center gap-3">
            <span className="px-2.5 py-1 bg-green-900/40 border border-green-500/40 rounded-lg text-xs text-green-300">
              {enabledCount} {t.admin?.enabledWidgets || 'enabled'}
            </span>
            <span className="px-2.5 py-1 bg-gray-800/50 border border-gray-600/40 rounded-lg text-xs text-gray-400">
              {widgetTypes.length} {t.admin?.totalWidgets || 'total'}
            </span>
          </div>
        </div>
        <p className="text-sm text-gray-400">
          {t.admin?.widgetConfigDesc || 'Drag widgets from left column to right to enable them.'}
        </p>
      </section>

      {/* Widget Editor */}
      <section>
        {widgetTypes.length > 0 ? (
          <WorkflowWidgetEditor
            widgetTypes={widgetTypes}
            selectedLanguage={language}
            workflowWidgets={workflowWidgets}
            onAdvancedWidgetToggle={handleWidgetToggle}
            onDisplayOrderChange={handleDisplayOrderChange}
            dynamicWidget={hasDynamicWidget ? formData.dynamic_widget : null}
            onDynamicWidgetClick={onNavigateToDynamicWidget}
            onDynamicWidgetOrderChange={handleDynamicWidgetOrderChange}
            mode="edit"
          />
        ) : (
          <div className="text-center py-12 bg-gray-800/30 rounded-xl border border-gray-700/50">
            <Icon name={ICONS.GRID} size={48} className="mx-auto mb-4 text-gray-600" />
            <p className="text-gray-400 mb-2">
              {t.admin?.noWidgetTypes || 'No widget types available'}
            </p>
            <p className="text-sm text-gray-500">
              {t.admin?.noWidgetTypesDesc || 'Widget types must be configured in the system first.'}
            </p>
          </div>
        )}
      </section>
    </div>
  );
}
